import type { ReactNode, RefObject } from 'react';
import { MapPinIcon } from '@phosphor-icons/react';
import { DrawerFooter, DrawerHeader, DrawerTitle } from '@voll-intelligence/ui-kit/ui/drawer';
import { BottomSheet } from '../../shared/bottom_sheet.js';

/**
 * A busca de hotel no palco ESTREITO (celular, iframe do host web) — a leitura
 * do Airbnb. Fechada, uma pílula-resumo só: destino em destaque, uma legenda
 * com datas e hóspedes, e a lupa na ponta. Aberta, uma folha sobe do rodapé
 * com os três campos empilhados em cartões: o cartão em edição expande e
 * mostra o controle de verdade; os outros dois ficam numa linha (rótulo à
 * esquerda, valor à direita) e um toque troca o cartão aberto.
 *
 * Só a PELE mora aqui. Quem decide qual cartão está aberto, o que vai dentro
 * de cada um e o que a lupa faz é a barra (`hotel_search_bar.tsx`), que
 * reaproveita nos cartões os mesmos painéis dos popovers da casca larga — o
 * gesto é um só, o layout é que muda com a largura.
 */

export type HotelSearchSheetField = 'rooms' | 'places' | 'dates';

export type HotelSearchSheetCard = {
  readonly field: HotelSearchSheetField;
  /** A PERGUNTA do campo (`Quem`, `Onde`, `Quando`): título do cartão aberto e
   *  rótulo do cartão fechado. */
  readonly label: string;
  /** O resumo atual, mostrado no cartão fechado. */
  readonly value: string;
  /** O valor ainda não respondido — um degrau mais claro. */
  readonly muted?: boolean;
  /** O gatilho do cartão fechado: é pra ele que o foco volta quando o painel
   *  fecha por Escape, como na casca larga. */
  readonly triggerRef?: RefObject<HTMLButtonElement | null>;
  readonly content: ReactNode;
};

/**
 * A pílula fechada. `title`/`caption` já vêm resolvidos pela barra — a pílula
 * não sabe o que é destino ou data, só que há um texto forte e uma legenda.
 * `action` é a lupa, montada pela barra com o mesmo `Button` da casca larga.
 */
export function HotelSearchSummary({
  rootRef,
  triggerRef,
  title,
  caption,
  muted,
  expanded,
  disabled,
  onOpen,
  action,
}: {
  readonly rootRef: RefObject<HTMLDivElement | null>;
  /** O botão da pílula — é pra ele que a folha devolve o foco ao fechar. */
  readonly triggerRef: RefObject<HTMLButtonElement | null>;
  readonly title: string;
  readonly caption: string;
  readonly muted: boolean;
  readonly expanded: boolean;
  readonly disabled: boolean;
  readonly onOpen: () => void;
  readonly action: ReactNode;
}): ReactNode {
  return (
    <div
      ref={rootRef}
      className="relative flex w-full items-center gap-1.5 rounded-full bg-card/95 p-1.5 shadow-md ring-1 ring-foreground/10 backdrop-blur-sm"
    >
      {/* `data-hotel-search-summary`: a porta que "Ajustar busca" do resultado
          usa pra abrir a folha, como usa a pílula do destino na casca larga. */}
      <button
        ref={triggerRef}
        type="button"
        data-hotel-search-summary=""
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={expanded}
        onClick={onOpen}
        className="flex h-9 min-w-0 flex-1 items-center gap-2.5 rounded-full pr-2 pl-2.5 text-left outline-none transition-colors hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50"
      >
        <MapPinIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="grid min-w-0 flex-1 leading-tight">
          <span
            className={`truncate text-sm ${muted ? 'text-muted-foreground' : 'font-medium text-foreground'}`}
          >
            {title}
          </span>
          {/* Espaço-texto entre as linhas: no grid ele não gera caixa, mas entra
              no nome acessível — sem ele o leitor de tela emenda destino e
              legenda numa palavra só. */}{' '}
          <span className="truncate text-xs text-muted-foreground">{caption}</span>
        </span>
      </button>
      {action}
    </div>
  );
}

function CollapsedCard({
  card: { triggerRef, field, label, muted, value },
  onActivate,
}: {
  readonly card: HotelSearchSheetCard;
  readonly onActivate: (field: HotelSearchSheetField) => void;
}): ReactNode {
  return (
    <button
      type="button"
      ref={triggerRef}
      aria-expanded={false}
      onClick={() => {
        onActivate(field);
      }}
      className="flex h-12 w-full shrink-0 items-center justify-between gap-3 rounded-2xl bg-card px-4 text-left shadow-xs ring-1 ring-foreground/10 outline-none transition-colors hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50"
    >
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      <span
        className={`min-w-0 truncate text-sm font-medium ${muted ? 'text-muted-foreground' : 'text-foreground'}`}
      >
        {value}
      </span>
    </button>
  );
}

function ExpandedCard({ card }: { readonly card: HotelSearchSheetCard }): ReactNode {
  return (
    <section
      aria-label={card.label}
      className="flex shrink-0 flex-col rounded-2xl bg-card p-4 shadow-xs ring-1 ring-foreground/10"
    >
      <h3 className="mb-3 text-lg font-semibold text-foreground">{card.label}</h3>
      {card.content}
    </section>
  );
}

/** A folha da busca do hotel no palco estreito: a `BottomSheet` da casca com o
 *  título, os cartões (um aberto, os outros em linha) e o Buscar no pé. */
export function HotelSearchSheet({
  open,
  onOpenChange,
  returnFocusTo,
  title,
  cards,
  activeField,
  onActivate,
  footer,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Ver `BottomSheet.returnFocusTo` — aqui, a pílula que a abriu. */
  readonly returnFocusTo: RefObject<HTMLElement | null>;
  readonly title: string;
  readonly cards: readonly HotelSearchSheetCard[];
  readonly activeField: HotelSearchSheetField | null;
  readonly onActivate: (field: HotelSearchSheetField) => void;
  /** O Buscar, montado pela barra. */
  readonly footer: ReactNode;
}): ReactNode {
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} returnFocusTo={returnFocusTo}>
      <DrawerHeader className="text-left!">
        <DrawerTitle>{title}</DrawerTitle>
      </DrawerHeader>
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto scrollbar-rail p-4 pr-[calc(1rem-var(--scrollbar-rail))]">
        {cards.map((card) =>
          card.field === activeField ? (
            <ExpandedCard key={card.field} card={card} />
          ) : (
            <CollapsedCard key={card.field} card={card} onActivate={onActivate} />
          ),
        )}
      </div>
      <DrawerFooter className="flex-row justify-end border-t border-border pt-3">
        {footer}
      </DrawerFooter>
    </BottomSheet>
  );
}
